import { Stack, Typography } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import {
  text,
  type Value,
} from '@/james-playground/aa-family-claim/claim/facts';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import {
  GATE,
  SUGGEST,
  SUPPORTING,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  EntryField,
  hasProblem,
  Part,
  Parts,
  SuggestField,
  Toggle,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  storedAs,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/** Their statement is written on the printed form, so the card does not ask for it. */
const WRITTEN_BY_HAND = 'supporterStatement';
const ORDER = [
  'supporterName',
  'supporterRelationship',
  'supporterFrequency',
  'supporterPhone',
  'supporterAddress',
  'supporterPostcode',
];

const same = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * "Ask someone who knows her to write a few lines" (Q55 to Q62). On a Yes,
 * who they are and how to reach them; the few lines themselves go on the
 * printed form in their own hand. The person signing the form cannot be the
 * one who writes it (rule R9.9), and the card stops on that.
 */
const SupportingCard = ({
  ask,
  facts,
  voice: v,
  onSubmit,
  onSkip,
}: CardProps) => {
  const { draft, set } = useDraft(ask, facts);
  const qid = useQids();
  const gateId = ask.entries[0];
  const gate = str(draft[gateId]);
  const fields = ORDER.filter((id) => ask.entries.includes(id));

  const signer =
    text(facts, 'signingForSomeoneElse') === 'Yes'
      ? text(facts, 'representativeName')
      : [text(facts, 'firstNames'), text(facts, 'surname')]
          .filter(Boolean)
          .join(' ');
  const clash = Boolean(signer) && same(str(draft.supporterName), signer);

  const ready =
    gate === 'No' ||
    (gate === 'Yes' &&
      str(draft.supporterName).trim() !== '' &&
      !clash &&
      !fields.some((id) => hasProblem(id, draft[id])));

  const submit = () => {
    if (gate !== 'Yes') return onSubmit({ [gateId]: 'No' }, SUPPORTING.noReply);
    const values: Record<string, Value> = {
      [gateId]: 'Yes',
      [WRITTEN_BY_HAND]: '',
    };
    for (const id of fields) {
      const value = storedAs(entry(id).answerType, str(draft[id]));
      if (value) values[id] = value;
    }
    onSubmit(
      values,
      [str(values.supporterName), str(values.supporterRelationship)]
        .filter(Boolean)
        .join(', '),
    );
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Toggle
        question={say(GATE[gateId] ?? ask.prompt, v)}
        hint={say(SUPPORTING.note, v)}
        value={gate}
        onChange={(value) => set(gateId, value)}
      />
      {gate === 'Yes' && (
        <Stack spacing={1.5} role='group' aria-labelledby={qid('about')}>
          <Typography id={qid('about')} variant='h6'>
            {SUPPORTING.about}
          </Typography>
          <Parts>
            {fields.map((id) => {
              const suggest = SUGGEST[id];
              if (suggest)
                return (
                  <Part key={id} qid={qid(id)} question={say(suggest.field, v)}>
                    <SuggestField
                      labelledBy={qid(id)}
                      chips={suggest.chips}
                      other={suggest.other}
                      field={say(suggest.field, v)}
                      value={str(draft[id])}
                      onChange={(value) => set(id, value)}
                    />
                  </Part>
                );
              return (
                <EntryField
                  key={id}
                  id={id}
                  v={v}
                  value={str(draft[id])}
                  onChange={(value) => set(id, value)}
                  error={
                    id === 'supporterName' && clash
                      ? say(SUPPORTING.sameAsSigner, v)
                      : undefined
                  }
                />
              );
            })}
          </Parts>
        </Stack>
      )}
    </CardShell>
  );
};

SupportingCard.displayName = 'SupportingCard';
export default SupportingCard;
